import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useSignedFunction from "@/shared/hooks/useSignedFunction";
import { LikeEntity } from "../types";

export interface LikeState {
  entityId: string | undefined;
  type: LikeEntity;
  likesApi: {
    toggleLike: any;
    getLikeCount: any;
  };
  isMeLiking?: boolean;
}

const useLike = ({
  entityId,
  type,
  likesApi,
  isMeLiking = false,
}: LikeState) => {
  const [isLikedByMe, setIsLikedByMe] = useState(isMeLiking);

  const queryClient = useQueryClient();

  useEffect(() => {
    setIsLikedByMe(isMeLiking);
  }, [isMeLiking]);

  const { data: likeCount } = useQuery({
    queryKey: [`${type}-like-count`, entityId],
    queryFn: () => likesApi.getLikeCount(entityId),
  });

  const { mutateAsync: toggleLike } = useMutation({
    mutationFn: () => likesApi.toggleLike(entityId),
    onMutate: () => {
      queryClient.cancelQueries({
        queryKey: [`${type}-like-count`, entityId],
      });

      const previousNftLikesCount = queryClient.getQueryData([
        `${type}-like-count`,
        entityId,
      ]);

      queryClient.setQueryData(
        [`${type}-like-count`, entityId],
        (old?: any) => {
          if (!entityId) return old;
          if (isLikedByMe) {
            return {
              ...old,
              [entityId]: (+old[entityId] || 0) - 1,
            };
          }
          return {
            ...old,
            [entityId]: (+old[entityId] || 0) + 1,
          };
        },
      );

      setIsLikedByMe((prevState) => !prevState);

      return { previousNftLikesCount };
    },
    onError: (_e, _v, context) => {
      queryClient.setQueryData(
        [`${type}-like-count`, entityId],
        context?.previousNftLikesCount,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries([`${type}-like-count`, entityId]);
    },
  });

  const checkUserAndToggleLike = useSignedFunction(toggleLike);
  return {
    likeCount: entityId && likeCount?.[entityId] ? likeCount[entityId] : 0,
    isLikedByMe,
    setIsLikedByMe,
    toggleLike: checkUserAndToggleLike,
  };
};

export default useLike;
